/**
 * DriverSiteAssignmentModal - Assign a working site to a driver
 *
 * Allows selecting a working site from the available sites
 * and assigning it to the driver.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../api.js';
import { MapPin, X } from 'lucide-react';

// Site type → i18n key suffix under drivers.siteAssignmentModal.siteTypes.*
const SITE_TYPE_KEYS = {
  headquarters: 'headquarters',
  branch: 'branch',
  depot: 'depot',
  parking: 'parking',
  workshop: 'workshop',
  client_site: 'clientSite',
  temporary: 'temporary',
  siege: 'headquarters',
  agence: 'branch',
  station: 'station',
  chantier: 'worksite',
  autre: 'other'
};

function DriverSiteAssignmentModal({ isOpen, onClose, onSuccess, onMessage, driver, sites }) {
  const { t } = useTranslation();
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loading, setLoading] = useState(false);

  const siteTypeLabel = (type) => {
    const key = SITE_TYPE_KEYS[type];
    return key ? t(`drivers.siteAssignmentModal.siteTypes.${key}`) : type;
  };

  // Reset state whenever modal opens or driver changes
  useEffect(() => {
    if (isOpen) {
      // Always sync with current driver.working_site_id when modal opens
      setSelectedSiteId(driver?.working_site_id ? String(driver.working_site_id) : '');
      setLoading(false);
    }
  }, [isOpen, driver?.working_site_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      // Update driver with new working site
      const updateData = {
        working_site_id: selectedSiteId ? parseInt(selectedSiteId) : null
      };

      await api.driver.update(driver.id, updateData);

      // Get site name for confirmation message
      const selectedSite = sites.find(s => s.id === parseInt(selectedSiteId));
      const siteName = selectedSite?.site_name || '';
      const name = `${driver.first_name} ${driver.last_name}`;

      onMessage && onMessage({
        title: t('drivers.messages.successTitle'),
        message: selectedSiteId
          ? t('drivers.siteAssignmentModal.assigned', { site: siteName, name })
          : t('drivers.siteAssignmentModal.unassigned', { name }),
        type: 'success'
      });

      // Trigger refresh FIRST (before closing modal)
      if (onSuccess) {
        await onSuccess();
      }
      // Then close modal after refresh completes
      onClose();
    } catch (error) {
      console.error('Error assigning site:', error);
      onMessage && onMessage({
        title: t('drivers.messages.errorTitle'),
        message: t('drivers.siteAssignmentModal.updateError'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Find current site (handle both integer and string comparison)
  const currentSiteId = driver?.working_site_id;
  const currentSite = currentSiteId ? sites.find(s => s.id === parseInt(currentSiteId)) : null;
  const activeSites = sites.filter(s => s.status === 'active' || s.status === undefined);
  // Le choix se lit comme l'organigramme (« Direction des Collèges → U.T. Sud ») : les sites
  // rattachés suivent leur parent, et le libellé porte le parent.
  const byId = new Map(sites.map(s => [s.id, s]));
  const siteLabel = (site) => {
    const parent = site.parent_site_id ? byId.get(site.parent_site_id) : null;
    return parent ? `${parent.site_name} → ${site.site_name}` : site.site_name;
  };
  const orderedSites = [...activeSites].sort((a, b) => siteLabel(a).localeCompare(siteLabel(b), 'fr'));
  const na = t('common.states.notAvailable');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="bg-purple-500 text-white px-6 py-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold">{t('drivers.siteAssignmentModal.title')}</h3>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            {/* Driver info */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-600 mb-1">{t('drivers.siteAssignmentModal.driver')}</p>
              <p className="font-semibold text-gray-900">{driver?.first_name} {driver?.last_name}</p>
              <p className="text-sm text-gray-600">{driver?.email}</p>
            </div>

            {/* Current site */}
            <div className="bg-purple-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-600 mb-1">{t('drivers.siteAssignmentModal.currentSite')}</p>
              {currentSite ? (
                <>
                  <p className="font-semibold text-gray-900">{currentSite.site_name}</p>
                  <p className="text-xs text-gray-600">
                    {siteTypeLabel(currentSite.site_type)} - {currentSite.city || na}
                  </p>
                </>
              ) : (
                <p className="text-gray-500 italic">{t('drivers.siteAssignmentModal.noSite')}</p>
              )}
            </div>

            {/* Site selector */}
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('drivers.siteAssignmentModal.newSite')}
            </label>
            <select
              value={selectedSiteId}
              onChange={(e) => setSelectedSiteId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">{t('drivers.siteAssignmentModal.noSiteOption')}</option>
              {orderedSites.map(site => (
                <option key={site.id} value={site.id}>
                  {siteLabel(site)} ({siteTypeLabel(site.site_type)}) - {site.city || na}
                </option>
              ))}
            </select>

            {/* Info about selected site */}
            {selectedSiteId && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                {(() => {
                  const site = sites.find(s => s.id === parseInt(selectedSiteId));
                  if (!site) return null;
                  return (
                    <div className="space-y-1">
                      <p className="font-medium text-gray-900">{site.site_name}</p>
                      {site.address && <p className="text-gray-600">{site.address}</p>}
                      <p className="text-gray-600">{site.postal_code} {site.city}</p>
                      {site.contact_name && (
                        <p className="text-gray-500">{t('drivers.siteAssignmentModal.contact', { name: site.contact_name })}</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              disabled={loading}
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('common.states.saving')}
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4" />
                  {t('common.actions.save')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DriverSiteAssignmentModal;
